/**
 * How much we believe an organization owns the GS1 company prefix it claims.
 *
 * §12 calls GTIN ownership *"the trust problem"*: tenant B must not claim tenant A's
 * GTIN, and neither should claim Coca-Cola's. This vocabulary is the state of that
 * question for one prefix, and it is the gate publishing depends on — an unverified
 * prefix means watermarked previews at **every** tier, and a paid plan never lifts it.
 * That is invariant 4: a trust gate, not a price gate.
 *
 * Same three-part treatment as every other enumerated column here (ruled 2026-08-04):
 * `text` + `CHECK` in SQL, this constant, and `constraints.test.ts` asserting the two
 * are set-equal — plus, since c13, a **writability round-trip**, because two copies of
 * a vocabulary agreeing says nothing about whether the values can be stored.
 */
export const VERIFICATIONS = [
    /** Claimed and nothing more. The default, and the state most prefixes start in. */
    "unverified",
    /** Evidence submitted, awaiting human review. The launch path is document review. */
    "pending",
    /**
     * Confirmed against GS1's registry — the automated path (§15.1). **Nothing produces
     * this value yet:** registry API access is an open question with a long lead time,
     * and Phase 8 is where automation lands. The value exists now so the state machine
     * does not need widening later, and so a row verified by document is
     * distinguishable forever from one verified by machine.
     */
    "verified_registry",
    /** Confirmed by a human reading an uploaded GS1 licence certificate. The launch path. */
    "verified_document",
    /**
     * A second organization claimed this prefix. §12: *"existing verified claim always
     * wins pending evidence"* — this state is about the challenger, not the holder, and
     * every transition is audit-logged.
     */
    "disputed",
];
/**
 * The two states that permit publishing. §12(2): publishing — live resolution and
 * unwatermarked artwork — requires the GTIN to fall under a **verified** prefix.
 *
 * A derived set rather than a second list, so adding a verification value cannot
 * silently add a way to publish: a new value is excluded until someone edits this line
 * deliberately.
 */
export const PUBLISHABLE_VERIFICATIONS = VERIFICATIONS.filter((v) => v.startsWith("verified_"));
/** Does this verification state permit going live? */
export function permitsPublishing(verification) {
    return PUBLISHABLE_VERIFICATIONS.includes(verification);
}
/**
 * How an organization came to claim one GTIN.
 *
 * `gtin_claims.via` — the provenance of the claim, kept because a dispute is decided on
 * evidence and *"they typed it in"* and *"it falls under their verified prefix"* are
 * very different evidence.
 */
export const CLAIM_ROUTES = [
    /** The GTIN falls under a company prefix this org holds. The ordinary path. */
    "prefix",
    /**
     * A single GS1-issued GTIN with no prefix licence behind it — the smallest GS1
     * product, and common for a brand with one or two SKUs.
     */
    "single_gtin",
    /** Staff resolved a dispute in this org's favour. Always audit-logged. */
    "manual_override",
];
//# sourceMappingURL=verification.js.map